import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AgentAvatar } from "@/components/AgentAvatar";
import CleaningSettingsCard from "@/components/CleaningSettings";
import { parseAiContent } from "@/lib/parseAiSettings";
import { Ion } from "@/components/Ion";
import { Screen, useBack } from "@/components/Orig";
import { colors } from "@/lib/theme";

const onboardKey = (id) => `alr_guide_onboarded_${id}`;
export const isGuideOnboarded = (id) => localStorage.getItem(onboardKey(id)) === "1";
const markOnboarded = (id) => localStorage.setItem(onboardKey(id), "1");

const HELP = [
  ["🚀 Where do I start?", "I just logged in for the first time — where should I start?"],
  ["🧑‍🏫 Where's the AI cleaning expert?", "Where do I find the AI that helps me with laser cleaning settings?"],
  ["📈 How do I price a job?", "How do I price a new laser cleaning job?"],
  ["🛡️ Get my LSO certificate", "How do I earn my LSO (Laser Safety) certificate?"],
  ["📚 Operations Manual", "Where is the Operations Manual?"],
  ["💬 Ask the community", "How do I ask other franchisees a question?"],
  ["🛒 Buy equipment / consumables", "Where do I buy ALR-approved consumables and equipment?"],
];
const ONBOARD = [
  ["👍 Yes, give me the tour", "Yes — give me the quick tour."],
  ["⏭️ Show me the next feature", "Sounds good. What's next?"],
  ["❓ Why would I use this?", "Why would I use this feature?"],
  ["✅ I'm good — let me explore", "I think I'm good. Let me explore on my own from here."],
];
const md = { p: ({ node, ...p }) => <p className="mb-2 last:mb-0" {...p} />, ul: ({ node, ...p }) => <ul className="mb-2 list-disc pl-5" {...p} />, ol: ({ node, ...p }) => <ol className="mb-2 list-decimal pl-5" {...p} />, strong: ({ node, ...p }) => <strong className="font-bold text-white" {...p} />, pre: ({ node, ...p }) => <pre className="whitespace-pre-wrap break-words" {...p} /> };

const Bubble = ({ m, i, onGo }) => (
  <div className="mb-2">
    <div className={`max-w-[85%] overflow-hidden break-words rounded-[14px] px-3.5 py-2.5 text-sm leading-5 ${m.role === "user" ? "ml-auto bg-[#4CCC06] text-black" : "border border-[#333] bg-[#1A1A1A]"}`} data-testid={`guide-msg-${i}`}>
      {m.role === "user" ? <p className="whitespace-pre-wrap">{m.text}</p> : parseAiContent(m.text).map((seg, si) => seg.kind === "settings" ? <CleaningSettingsCard key={si} data={seg.data} /> : <ReactMarkdown key={si} remarkPlugins={[remarkGfm]} components={md}>{seg.text}</ReactMarkdown>)}
    </div>
    {m.role === "assistant" && m.suggestions?.length > 0 && (
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {m.suggestions.map((s, j) => (
          <button key={j} onClick={() => onGo(s.route)} data-testid={`guide-suggestion-${j}`} className="flex max-w-full items-center gap-1 rounded-full bg-[#4CCC06] px-3 py-1.5 text-xs font-bold text-black">
            <Ion name="arrow-forward" size={13} color="#000" /><span className="truncate">{s.label}</span>
          </button>
        ))}
      </div>
    )}
  </div>
);

export default function Guide() {
  const [params] = useSearchParams();
  const mode = params.get("mode") === "onboarding" ? "onboarding" : "help";
  const { user } = useAuth();
  const navigate = useNavigate();
  const back = useBack();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const booted = useRef(false);
  const endRef = useRef(null);

  useEffect(() => { booted.current = false; setMessages([]); }, [user?.id, mode]);
  useEffect(() => {
    if (booted.current || mode !== "onboarding") return;
    booted.current = true;
    setSending(true);
    api("/guide/chat", { method: "POST", body: { message: "Hi! I just logged in for the first time. Could you welcome me and offer me a quick tour?", history: [], mode: "onboarding" } })
      .then((r) => setMessages([{ role: "assistant", text: r.reply, suggestions: r.suggestions }]))
      .catch((e) => setMessages([{ role: "assistant", text: `Welcome to the ALR Portal! I'm your ALR Guide — I'll help you find your way around. (Couldn't reach the AI right now: ${e.message || "try again in a moment"}.)` }]))
      .finally(() => setSending(false));
  }, [mode, messages]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = useCallback(async (text) => {
    if (!text.trim() || sending) return;
    const um = { role: "user", text: text.trim() };
    const history = messages.slice(-10).map((m) => ({ role: m.role, text: m.text }));
    setMessages((m) => [...m, um]); setInput(""); setSending(true);
    try {
      const r = await api("/guide/chat", { method: "POST", body: { message: um.text, history, mode } });
      setMessages((m) => [...m, { role: "assistant", text: r.reply || "(no reply)", suggestions: r.suggestions }]);
    } catch (e) { setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${e.message || "Sorry — try again."}` }]); }
    finally { setSending(false); }
  }, [messages, mode, sending]);

  const go = (route) => { if (mode === "onboarding") markOnboarded(user.id); navigate(route); };
  const dismiss = () => { markOnboarded(user.id); navigate("/dashboard", { replace: true }); };
  const prompts = mode === "onboarding" ? ONBOARD : HELP;

  return (
    <Screen testId="guide-page" className="flex h-screen flex-col">
      <div className="flex items-center gap-2 p-4 pb-2">
        <button onClick={() => (mode === "onboarding" ? dismiss() : back())} aria-label="Back" className="p-2" data-testid="back-btn"><Ion name="chevron-back" size={22} color="#fff" /></button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[10px] font-bold tracking-[1px] text-[#4CCC06]">{mode === "onboarding" ? "WELCOME ABOARD" : "ALR GUIDE"}</p>
          <h1 className="truncate text-2xl font-bold" data-testid="page-title">{mode === "onboarding" ? "Let me show you around" : "How can I help?"}</h1>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {messages.length === 0 && mode === "help" && (
          <div className="flex flex-col items-center rounded-[14px] border border-[#333] bg-[#1A1A1A] p-6 text-center" data-testid="guide-welcome">
            <span className="rounded-full p-2" style={{ background: `${colors.accent}1A`, boxShadow: `0 0 40px ${colors.accent}40` }}><AgentAvatar kind="sales_coach" size={104} ring={colors.accent} /></span>
            <p className="mt-4 text-xl font-extrabold">Your ALR Guide</p>
            <p className="mt-2 max-w-md text-[13px] leading-5 text-[#A0A0A0]">Ask me anything about the app — where to find a feature, how to use it, or where to start a workflow. I can also open the right screen for you.</p>
          </div>
        )}
        {messages.map((m, i) => <Bubble key={i} m={m} i={i} onGo={go} />)}
        {sending && <div className="flex w-fit items-center gap-2 rounded-[14px] border border-[#333] bg-[#1A1A1A] px-3.5 py-2.5 text-sm"><Loader2 className="animate-spin text-[#4CCC06]" size={16} />Thinking…</div>}
        <div ref={endRef} />
      </div>
      {messages.length < 4 && !sending && (
        <div className="flex h-[52px] items-center gap-2 overflow-x-auto px-4">
          {prompts.map(([label, prompt], i) => <button key={i} onClick={() => send(prompt)} data-testid={`guide-quick-${i}`} className="shrink-0 rounded-full border border-[#333] bg-[#1A1A1A] px-3 py-2 text-xs font-semibold">{label}</button>)}
        </div>
      )}
      <div className="flex items-end gap-2 border-t border-[#333] p-3">
        <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }} rows={1} disabled={sending}
          placeholder={mode === "onboarding" ? "Reply to the Guide…" : "Ask the ALR Guide…"} data-testid="guide-input"
          className="max-h-32 flex-1 resize-none rounded-xl border border-[#333] bg-[#1A1A1A] px-3.5 py-2.5 text-sm text-white placeholder:text-[#A0A0A0] focus:outline-none" />
        <button disabled={sending || !input.trim()} onClick={() => send(input)} data-testid="guide-send" className="flex h-11 w-11 items-center justify-center rounded-full" style={{ background: input.trim() ? colors.accent : "#242424" }}>
          <Ion name="send" size={18} color={input.trim() ? "#000" : colors.textSecondary} />
        </button>
      </div>
      {mode === "onboarding" && (
        <div className="flex gap-2 px-3 pb-3">
          <button onClick={dismiss} data-testid="guide-skip-onboarding" className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-[#333] py-2.5 text-xs text-[#A0A0A0]"><Ion name="close-circle-outline" size={14} color={colors.textSecondary} />Skip tour</button>
          <button onClick={dismiss} data-testid="guide-done-onboarding" className="flex flex-[2] items-center justify-center gap-1 rounded-lg bg-[#4CCC06] py-2.5 text-xs font-extrabold text-black"><Ion name="checkmark-circle-outline" size={14} color="#000" />I'm comfortable — don't show again</button>
        </div>
      )}
    </Screen>
  );
}
